import React, { useEffect, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import {
    RenderPdfPrescription,
    TemplateV2,
    TemplateConfig,
} from '../../../Templar/RenderPdfPrescription';
import { track } from '../../../tracking';
import { LocalTemplateConfig, NO_FOOTER, NO_HEADER } from '../EditTemplate';
import {
    FOOTER_CONTAINER,
    HEADER_CONTAINER,
    getBodyHtml,
    getCustomFooterHtml,
    getCustomHeaderHtml,
    getFooterHtml,
    getHeadHtml,
    getHeaderHtml,
} from '../../../Templar/templateUtils';
import { captureError } from '../../captureError';
import { DoctorProfile } from '../../../api/fetchMyProfile';
import { getAddPDForms } from '../../utils';
import { DFormEntity } from '../../../api/fetchDoctorGenieByOID';
import { FLAVOUR } from '../../../constants';

// Define types for renderpdf
interface RenderPdfOptions {
    head: string;
    body_html: string;
    header_html?: string;
    footer_html?: string;
    page_size: string;
    margin_left?: string;
    margin_right?: string;
    header_height?: string;
    footer_height?: string;
    callback?: () => void;
}

interface RenderPdf {
    render: (options: RenderPdfOptions, targetSelector: string) => void;
}

declare global {
    interface Window {
        renderpdf: RenderPdf;
    }
}

export const RX_RENDER_PDF = 'rx-render-pdf';
export const RX_RENDER_PDF_DUMMY = 'rx-render-pdf-dummy';

export const getHeader = (
    docProfile: DoctorProfile,
    ptFormFields: DFormEntity[],
    render_pdf_config?: TemplateConfig,
    rxLocalConfig?: LocalTemplateConfig,
    activeClinic?: string,
    data?: RenderPdfPrescription,
    rxConfig?: TemplateV2,
): JSX.Element => {
    if (render_pdf_config?.header_img) {
        return getCustomHeaderHtml(
            render_pdf_config,
            ptFormFields,
            rxLocalConfig,
            render_pdf_config?.header_img === NO_HEADER ? undefined : render_pdf_config?.header_img,
            data,
            rxConfig,
        );
    }

    return getHeaderHtml(
        docProfile,
        ptFormFields,
        render_pdf_config,
        rxLocalConfig,
        activeClinic,
        data,
        rxConfig,
    );
};

export const getFooter = (
    docProfile: DoctorProfile,
    data: RenderPdfPrescription,
    rxLocalConfig?: LocalTemplateConfig,
    renderPdfConfig?: TemplateConfig,
    isHideFooterDetails?: boolean,
): JSX.Element => {
    if (renderPdfConfig?.footer_img) {
        return getCustomFooterHtml(
            docProfile,
            data,
            rxLocalConfig,
            renderPdfConfig?.footer_top_margin,
            renderPdfConfig?.footer_bottom_margin,
            renderPdfConfig?.footer_left_margin,
            renderPdfConfig?.footer_right_margin,
            renderPdfConfig?.footer_img === NO_FOOTER ? undefined : renderPdfConfig?.footer_img,
            renderPdfConfig?.show_signature,
            renderPdfConfig?.show_name_in_signature,
            renderPdfConfig?.show_signature_text,
            renderPdfConfig?.show_page_number,
            renderPdfConfig?.show_prescription_id,
            renderPdfConfig.show_not_valid_for_medical_legal_purpose_message,
            renderPdfConfig.show_eka_logo,
            renderPdfConfig.attachment_image,
            renderPdfConfig?.footer_doctor_name_color,
            isHideFooterDetails,
            undefined,
            renderPdfConfig.show_approval_details,
            renderPdfConfig.footer_height,
            renderPdfConfig.floating_footer_details,
        );
    }

    return getFooterHtml(docProfile, data, rxLocalConfig, renderPdfConfig);
};

const RxTemplatePdfView: React.FC<{
    docProfile: DoctorProfile;
    data: RenderPdfPrescription;
    rxConfig: TemplateV2;
    activeClinic: string | undefined;
    rxLocalConfig?: LocalTemplateConfig;
    saveRxConfigInState: (rxObj: TemplateConfig) => void;
    locallyCreatedPdf?: boolean;
}> = ({
    docProfile,
    data,
    rxConfig,
    activeClinic,
    rxLocalConfig,
    saveRxConfigInState,
    locallyCreatedPdf = false,
}) => {
    const renderPdfConfig = rxConfig?.render_pdf_config;
    const headerRef = useRef<HTMLDivElement | null>();
    const footerRef = useRef<HTMLDivElement | null>();
    const scrollOffset = useRef<number>(0);
    const ref = useRef<
        { func: (rxObj: TemplateConfig) => void; rxConfig: TemplateV2 } | undefined
    >();
    const [isReady, setIsReady] = useState(false);
    const renderTimeoutRef = useRef<NodeJS.Timeout>();
    const prevTopMarginRef = useRef<number>(0);
    const prevBottomMarginRef = useRef<number>(0);
    const prevFooterTopMarginRef = useRef<number>(0);
    const prevFooterBottomMarginRef = useRef<number>(0);
    const [ptFormField, setPtFormFields] = useState<DFormEntity[]>([]);

    useEffect(() => {
        const fetchPtFormFields = async () => {
            const fields = await getAddPDForms();
            setPtFormFields(fields);
        };
        fetchPtFormFields();
    }, []);

    ref.current = {
        func: saveRxConfigInState,
        rxConfig: rxConfig,
    };

    useEffect(() => {
        // Set ready state after component mount
        setIsReady(true);
        return () => {
            setIsReady(false);
            if (renderTimeoutRef.current) {
                clearTimeout(renderTimeoutRef.current);
            }
        };
    }, []);

    const renderPdf = (targetId: string, options: RenderPdfOptions) => {
        return new Promise<void>((resolve, reject) => {
            const element = document.getElementById(targetId);
            if (!element) {
                reject(new Error(`Target element #${targetId} not found`));
                return;
            }

            requestAnimationFrame(() => {
                try {
                    if (!window.renderpdf?.render) {
                        throw new Error('renderpdf.render is not available');
                    }
                    window.renderpdf.render(options, `#${targetId}`);
                    resolve();
                } catch (error) {
                    console.error('Error in renderPdf:', error);
                    reject(error);
                }
            });
        });
    };

    // dummy portion at the end is to make sure the template image is updated one not cached one
    useEffect(() => {
        if (!renderPdfConfig || !isReady) {
            console.log('oops renderpdf config not found in useEffect of rxtemplatepdfView.tsx');
            return;
        }

        // Clear any existing render timeout
        if (renderTimeoutRef.current) {
            clearTimeout(renderTimeoutRef.current);
        }

        // Set a small delay to ensure DOM is ready
        renderTimeoutRef.current = setTimeout(async () => {
            try {
                const headerHtml = getHeader(
                    docProfile,
                    ptFormField,
                    renderPdfConfig,
                    rxLocalConfig,
                    activeClinic,
                    data,
                    rxConfig,
                );
                const footerHtml = getFooter(
                    docProfile,
                    data,
                    rxLocalConfig,
                    renderPdfConfig,
                    !renderPdfConfig?.floating_footer && renderPdfConfig?.floating_footer_details,
                );

                const footerDetailsHtml = renderPdfConfig?.floating_footer_details ? (
                    getCustomFooterHtml(
                        docProfile,
                        data,
                        rxLocalConfig,
                        renderPdfConfig?.footer_top_margin,
                        renderPdfConfig?.footer_bottom_margin,
                        renderPdfConfig?.footer_left_margin,
                        renderPdfConfig?.footer_right_margin,
                        renderPdfConfig?.footer_img === NO_FOOTER
                            ? undefined
                            : renderPdfConfig?.footer_img,
                        renderPdfConfig?.show_signature,
                        renderPdfConfig?.show_name_in_signature,
                        renderPdfConfig?.show_signature_text,
                        renderPdfConfig?.show_page_number,
                        renderPdfConfig?.show_prescription_id,
                        renderPdfConfig.show_not_valid_for_medical_legal_purpose_message,
                        renderPdfConfig.show_eka_logo,
                        renderPdfConfig.attachment_image,
                        renderPdfConfig?.footer_doctor_name_color,
                        false,
                        true,
                        renderPdfConfig.show_approval_details,
                        undefined,
                        renderPdfConfig.floating_footer_details,
                    )
                ) : (
                    <></>
                );

                // Render main PDF
                //asdf
                const mainRenderOptions: RenderPdfOptions = {
                    head: getHeadHtml(
                        'en',
                        renderPdfConfig?.prescription_size || 'normal',
                        true,
                        "<link rel='stylesheet' href='/fonts/template/fonts.css'>",
                    ),
                    body_html: renderToStaticMarkup(
                        <>
                            {renderPdfConfig?.floating_header ? headerHtml : <></>}
                            {/* {renderPdfConfig?.floating_patient_details ? (
                                repetitivePtDetails
                            ) : (
                                <></>
                            )} */}
                            {getBodyHtml(data, false, rxConfig, ptFormField, FLAVOUR)}
                            {renderPdfConfig?.floating_footer ? footerHtml : footerDetailsHtml}
                        </>,
                    ), //here
                    header_html: renderPdfConfig?.floating_header
                        ? // renderPdfConfig?.floating_patient_details
                          //     ? renderToStaticMarkup(repetitivePtDetails)
                          //     :
                          undefined
                        : renderToStaticMarkup(
                              //   renderPdfConfig?.floating_patient_details ? (
                              //       <>
                              //           {headerHtml}
                              //           {repetitivePtDetails}
                              //       </>
                              //   ) : (
                              headerHtml,
                              // ),
                          ),
                    footer_html: renderPdfConfig?.floating_footer
                        ? undefined
                        : renderToStaticMarkup(footerHtml),
                    page_size: 'A4',
                    margin_left: renderPdfConfig?.margin_left,
                    margin_right: renderPdfConfig?.margin_right,
                    header_height: renderPdfConfig?.floating_header
                        ? renderPdfConfig?.floating_header_page_top_margin || '0cm'
                        : renderPdfConfig?.header_height,
                    footer_height: renderPdfConfig?.floating_footer
                        ? renderPdfConfig?.floating_footer_page_bottom_margin || '0cm'
                        : renderPdfConfig?.footer_height,
                    callback: () => {
                        const rxconf = document.getElementById('rx-render-pdf');
                        const ifr = rxconf?.getElementsByTagName('iframe');
                        const w = ifr?.[0]?.contentWindow;
                        w?.scrollTo(0, scrollOffset.current);

                        w?.addEventListener(
                            'scroll',
                            (e) => {
                                const offset =
                                    (e.currentTarget as Window)?.scrollY ||
                                    (e.currentTarget as Window)?.pageYOffset;
                                scrollOffset.current = offset;
                            },
                            { passive: true },
                        );
                    },
                };

                await renderPdf(RX_RENDER_PDF, mainRenderOptions);

                // Only render dummy if not locally created
                if (!locallyCreatedPdf) {
                    const dummyHeaderHtml = getHeader(
                        docProfile,
                        ptFormField,
                        renderPdfConfig,
                        undefined,
                        activeClinic,
                    );
                    const dummyFooterHtml = getFooter(
                        docProfile,
                        data,
                        undefined,
                        renderPdfConfig,
                        !renderPdfConfig?.floating_footer &&
                            renderPdfConfig?.floating_footer_details,
                    );

                    const dummyFooterDetailsHtml = renderPdfConfig?.floating_footer_details ? (
                        getCustomFooterHtml(
                            docProfile,
                            data,
                            rxLocalConfig,
                            renderPdfConfig?.footer_top_margin,
                            renderPdfConfig?.footer_bottom_margin,
                            renderPdfConfig?.footer_left_margin,
                            renderPdfConfig?.footer_right_margin,
                            renderPdfConfig?.footer_img === NO_FOOTER
                                ? undefined
                                : renderPdfConfig?.footer_img,
                            renderPdfConfig?.show_signature,
                            renderPdfConfig?.show_name_in_signature,
                            renderPdfConfig?.show_signature_text,
                            renderPdfConfig?.show_page_number,
                            renderPdfConfig?.show_prescription_id,
                            renderPdfConfig.show_not_valid_for_medical_legal_purpose_message,
                            renderPdfConfig.show_eka_logo,
                            renderPdfConfig.attachment_image,
                            renderPdfConfig?.footer_doctor_name_color,
                            false,
                            true,
                            renderPdfConfig.show_approval_details,
                            undefined,
                            renderPdfConfig.floating_footer_details,
                        )
                    ) : (
                        <></>
                    );

                    const dummyRenderOptions: RenderPdfOptions = {
                        head: getHeadHtml(
                            'en',
                            renderPdfConfig?.prescription_size || 'normal',
                            false,
                            "<link rel='stylesheet' href='/fonts/template/fonts.css'>",
                        ),
                        body_html: renderToStaticMarkup(
                            <>
                                {renderPdfConfig?.floating_header ? dummyHeaderHtml : <></>}
                                {getBodyHtml(data, false, rxConfig, ptFormField, FLAVOUR)}
                                {renderPdfConfig?.floating_footer
                                    ? dummyFooterHtml
                                    : dummyFooterDetailsHtml}
                            </>,
                        ),
                        header_html: renderPdfConfig?.floating_header
                            ? //  renderPdfConfig?.floating_patient_details
                              //     ? renderToStaticMarkup(repetitivePtDetails)
                              //     :
                              undefined
                            : renderToStaticMarkup(dummyHeaderHtml),
                        footer_html: renderPdfConfig?.floating_footer
                            ? undefined
                            : renderToStaticMarkup(dummyFooterHtml),
                        page_size: 'A4',
                        margin_left: renderPdfConfig?.margin_left,
                        margin_right: renderPdfConfig?.margin_right,
                        header_height: renderPdfConfig?.floating_header
                            ? '0cm'
                            : renderPdfConfig?.header_height,
                        footer_height: renderPdfConfig?.floating_footer
                            ? '0cm'
                            : renderPdfConfig?.footer_height,
                    };

                    await renderPdf(RX_RENDER_PDF_DUMMY, dummyRenderOptions);
                }
            } catch (e) {
                console.error('Error in PDF Render:', e);
                track('Error in PDF Render', { error: e as string });
                captureError(e as Error);
            }
        }, 100); // Small delay to ensure DOM is ready

        return () => {
            if (renderTimeoutRef.current) {
                clearTimeout(renderTimeoutRef.current);
            }

            // Only cleanup dummy if not locally created
            if (!locallyCreatedPdf) {
                const dummyRxRenderPdflist = document?.getElementById(RX_RENDER_PDF_DUMMY);
                dummyRxRenderPdflist?.firstElementChild &&
                    dummyRxRenderPdflist?.removeChild(
                        dummyRxRenderPdflist?.firstElementChild as Node,
                    );
            }

            const rxRenderPdflist = document?.getElementById(RX_RENDER_PDF);
            rxRenderPdflist?.firstElementChild &&
                rxRenderPdflist?.removeChild(rxRenderPdflist?.firstElementChild as Node);
        };
    }, [
        activeClinic,
        JSON.stringify(docProfile),
        JSON.stringify(rxConfig),
        JSON.stringify(renderPdfConfig),
        JSON.stringify(data),
        JSON.stringify(rxLocalConfig),
        isReady,
        locallyCreatedPdf,
    ]);
    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            const headerElement = headerRef.current?.querySelector(
                `#${HEADER_CONTAINER}`,
            ) as HTMLElement;

            if (!headerElement) return;

            const headerHeight = headerElement.offsetHeight;

            const topHeaderMargin =
                Number(
                    ref.current?.rxConfig?.render_pdf_config?.header_top_margin?.replace(
                        'cm',
                        '',
                    ) || 0,
                ) || 0;
            const bottomHeaderMargin =
                Number(
                    ref.current?.rxConfig?.render_pdf_config?.header_bottom_margin?.replace(
                        'cm',
                        '',
                    ) || 0,
                ) || 0;

            const offsetHeader = (headerHeight || 0) / 37.795275591;

            // Calculating the difference in margins
            const topMarginDiff = topHeaderMargin - prevTopMarginRef.current;
            const bottomMarginDiff = bottomHeaderMargin - prevBottomMarginRef.current;

            // Updating previous margins
            prevTopMarginRef.current = topHeaderMargin;
            prevBottomMarginRef.current = bottomHeaderMargin;

            // Adjusting the height based on margin changes
            const heightInCm = `${(offsetHeader + topMarginDiff + bottomMarginDiff).toFixed(1)}cm`;

            ref.current?.func?.({
                header_height: heightInCm,
            });
            //DONT REMOVE ELSE IT WILL CAUSE INFINITE LOOP
            resizeObserver.unobserve(headerRef.current as Element);
        });

        if (rxLocalConfig?.header_auto_adjust) {
            if (headerRef.current?.querySelector(`#${HEADER_CONTAINER}`)) {
                resizeObserver.observe(headerRef.current);
            }
        } else {
            resizeObserver.unobserve(headerRef.current as Element);
        }
    }, [
        renderPdfConfig?.floating_header,
        rxLocalConfig?.header_auto_adjust,
        renderPdfConfig?.header_top_margin,
        renderPdfConfig?.header_bottom_margin,
    ]);

    useEffect(() => {
        const resizeObserver = new ResizeObserver(() => {
            const footerElement = footerRef.current?.querySelector(
                `#${FOOTER_CONTAINER}`,
            ) as HTMLElement;

            if (!footerElement) return;

            const footerHeight = footerElement.offsetHeight;
            const topFooterMargin =
                Number(
                    ref.current?.rxConfig?.render_pdf_config?.footer_top_margin?.replace(
                        'cm',
                        '',
                    ) || 0,
                ) || 0;
            const bottomFooterMargin =
                Number(
                    ref.current?.rxConfig?.render_pdf_config?.footer_bottom_margin?.replace(
                        'cm',
                        '',
                    ) || 0,
                ) || 0;

            const offsetFooter = (footerHeight || 0) / 37.795275591;

            // Calculating the difference in margins
            const topMarginDiff = topFooterMargin - prevFooterTopMarginRef.current;
            const bottomMarginDiff = bottomFooterMargin - prevFooterBottomMarginRef.current;

            // Updating previous margins
            prevFooterTopMarginRef.current = topFooterMargin;
            prevFooterBottomMarginRef.current = bottomFooterMargin;

            // Adjusting the height based on margin changes
            const heightInCm = `${(offsetFooter + topMarginDiff + bottomMarginDiff).toFixed(1)}cm`;

            ref.current?.func?.({
                footer_height: heightInCm,
            });
            //DONT REMOVE ELSE IT WILL CAUSE INFINITE LOOP
            resizeObserver.unobserve(footerRef.current as Element);
        });

        if (rxLocalConfig?.footer_auto_adjust) {
            if (footerRef.current?.querySelector(`#${FOOTER_CONTAINER}`)) {
                resizeObserver.observe(footerRef.current);
            }
        } else {
            resizeObserver.unobserve(footerRef.current as Element);
        }
    }, [
        renderPdfConfig?.floating_footer,
        rxLocalConfig?.footer_auto_adjust,
        renderPdfConfig?.footer_top_margin,
        renderPdfConfig?.footer_bottom_margin,
    ]);

    return (
        <>
            {!locallyCreatedPdf && <div className="h-full" id={RX_RENDER_PDF}></div>}
            <div style={{ width: '210mm', height: '0px', overflowY: 'scroll' }}>
                <div
                    ref={(ref) => (headerRef.current = ref)}
                    style={{
                        marginLeft: rxConfig.render_pdf_config?.margin_left,
                        marginRight: rxConfig.render_pdf_config?.margin_right,
                    }}
                >
                    {getHeader(
                        docProfile,
                        ptFormField,
                        renderPdfConfig,
                        { header_border: true },
                        activeClinic,
                    )}
                </div>
            </div>
            <div style={{ width: '210mm', height: '0px', overflowY: 'scroll' }}>
                <div
                    ref={(ref) => (footerRef.current = ref)}
                    style={{
                        marginLeft: rxConfig.render_pdf_config?.margin_left,
                        marginRight: rxConfig.render_pdf_config?.margin_right,
                    }}
                >
                    {getFooter(
                        docProfile,
                        data,
                        { footer_border: true },
                        renderPdfConfig,
                        !renderPdfConfig?.floating_footer &&
                            renderPdfConfig?.floating_footer_details,
                    )}
                </div>
                {!locallyCreatedPdf && (
                    <div
                        className="h-full"
                        style={{
                            position: 'fixed',
                            zIndex: -1,
                        }}
                        id={RX_RENDER_PDF_DUMMY}
                    ></div>
                )}
            </div>
        </>
    );
};

export default RxTemplatePdfView;
